<template>
<fragment
    v-if="canChangeLayers">
    <div
        class="map-layers has-background-white"
        :class="{ 'are-tracks-visible': isTrackLayerVisible }"
        v-show="layersVisibility">
        <button class="button is-layer-selector is-block"
            :class="{ 'map-layer-selected': !isExtraLayerVisible }"
            @click="clearLayer">
            <p>Default</p>
        </button>
        <button
            v-if="canDisplayPanoramaLayer"
            class="button is-layer-selector is-block"
            :class="{ 'map-layer-selected': isPanoramaLayerVisible }"
            @click="showPanoramas">
            <p>Panoramas</p>
        </button>
        <button
            v-if="canDisplayTrackLayer"
            class="button is-layer-selector is-block"
            :class="{ 'map-layer-selected': isTrackLayerVisible }"
            @click="showTracks">
            <p>Tracks</p>
        </button>
        <div class="is-flex columns is-gapless is-align-items-center">
            <DatePicker
                class="column is-5"
                :date="startDate"
                @updateDate="updateStartDate"
            />
            <span class="column icon is-flex">
                <img src="../assets/right.svg" height="15px" width="15px" />
            </span>
            <DatePicker
                class="column is-5"
                :from="startDate"
                :date="endDate"
                @updateDate="updateEndDate"
            />
        </div>
        <button
            v-if="isTrackLayerVisible"
            class="button is-layer-selector is-block"
            @click="toggleDrawingMode">
            <p v-if="isDrawingModeActive">Close polygon drawing mode</p>
            <p v-else>Open polygon drawing mode</p>
        </button>
    </div>
    <button
        class="button mr-1 mb-1 is-navigate-small"
        :class="{ 'map-option-panel-open': layersVisibility }"
        @click="toggleLayersVisibility">
        <img
            src="../assets/layers.svg"
            :class="{ 'inverted': layersVisibility }"
            height="20px" width="20px"/>
    </button>
</fragment>
</template>

<script>
import debounce from 'debounce';
import { mapState, mapGetters } from 'vuex';

import TTMixin from '../mixins/TTMixin';
import DatePicker from './DatePicker.vue';
import { LAYERS } from '../store/modules/map';

/**
 * These props are also exported by the TTMap component.
 */
export const SharedProps = {
    /**
     * Initial extra layer in map.
     */
    initialLayer: {
        type: String,
        required: false,
    },

    /**
     * Initial start date for track filtering.
     */
    initialStartDate: {
        type: Date,
        required: false,
    },

    /**
     * Initial end date for track filtering.
     */
    initialEndDate: {
        type: Date,
        required: false,
    },
};

/**
 * Constants used within the component to display the extra layers.
 */
const TILE_SIZE = 256;
const LAYER = 'extra-layer';

/**
 * Adds extra layer to given map. The tiles are loaded from provided
 * tile URL.
 * @param {Object} map - The map object to which the layer will be
 *    added.
 * @param {Array} tiles - Array containing tile URLs for this layer. If
 *    `null` is passed, no layer is added.
 */
function addLayer(map, tiles) {
    if (!tiles) {
        return;
    }

    map.addSource(LAYER, {
        type: 'raster',
        tiles,
        tileSize: TILE_SIZE,
    });

    map.addLayer({
        id: LAYER,
        source: LAYER,
        type: 'raster',
    });
}

/**
 * Removes extra layer from the given `map`.
 */
function removeLayer(map) {
    const source = map.getSource(LAYER);
    if (source) {
        map.removeLayer(LAYER);
        map.removeSource(LAYER);
    }
}

export default {
    props: {
        ...SharedProps,

        map: {
            type: Object,
            required: false,
        },
    },

    mixins: [TTMixin],

    data() {
        return {
            /**
             * Contains the name of the layer to be displayed on the
             * map. `null` value indicates no layer.
             */
            layer: null,

            /**
             * Flag to control visibility of the map control layers panel.
             * Default is false for hide control layers panel.
             */
            layersVisibility: false,

            /**
             * Start date for track filtering.
             */
            startDate: null,
            /**
             * End date for track filtering.
             */
            endDate: null,
        };
    },

    computed: {
        ...mapGetters('permissions', {
            canDisplayTrackLayer: 'trackLayerPermission',
            canDisplayPanoramaLayer: 'panoramaLayerPermission',
        }),
        ...mapState('polygon', ['isDrawingModeActive']),

        /**
         * Returns `true` if user can change from default layer.
         */
        canChangeLayers() {
            return this.canDisplayTrackLayer || this.canDisplayPanoramaLayer;
        },

        /**
         * Returns `true` if this component displays the panorama layer.
         */
        isPanoramaLayerVisible() {
            return this.layer === LAYERS.PANORAMAS && this.canDisplayPanoramaLayer;
        },

        /**
         * Returns `true` if this component displays the track layer.
         */
        isTrackLayerVisible() {
            return this.layer === LAYERS.TRACKS && this.canDisplayTrackLayer;
        },

        /**
         * Returns `true` if either track or panorama layer is visible.
         */
        isExtraLayerVisible() {
            return this.isTrackLayerVisible || this.isPanoramaLayerVisible;
        },

        /**
         * Returns tile URLs for extra layer or `null` if no layer is
         * currently presented.
         * @return {Array?}
         */
        tiles() {
            if (!this.isExtraLayerVisible) {
                return null;
            }

            const { endpoint, parameters } = this.isPanoramaLayerVisible
                ? this.panorama
                : this.track;

            const url = this.momaURLBuilder(endpoint, {
                ...this.commonTileParameters,
                ...parameters,
            });

            return `${url}&bbox={bbox-epsg-3857}`;
        },

        commonTileParameters() {
            return {
                request: 'GetMap',
                width: TILE_SIZE,
                height: TILE_SIZE,
                version: '1.1.1',
                service: 'WMS',
                format: 'image/png',
                srs: 'EPSG:3857',
                transparent: true,
            };
        },

        panorama() {
            return {
                endpoint: 'panorama',
                parameters: {},
            };
        },

        track() {
            const { startDate, endDate } = this;
            const parameters = {
                ...(startDate ? { fromdate: startDate.toISOString() } : {}),
                ...(endDate ? { todate: endDate.toISOString() } : {}),
            };

            return {
                endpoint: 'track',
                parameters,
            };
        },
    },

    created() {
        this.layer = this.initialLayer;
        this.startDate = this.initialStartDate;
        this.endDate = this.initialEndDate;
    },

    methods: {
        /**
         * Shows the panorama layer on the map.
         */
        showPanoramas() {
            this.layer = LAYERS.PANORAMAS;
        },

        /**
         * Shows the track layer on the map.
         */
        showTracks() {
            this.layer = LAYERS.TRACKS;
        },

        /**
         * Clears extra layer on the map.
         */
        clearLayer() {
            this.layer = null;
        },

        /**
         * Helper function that will force the extra layer to be
         * reloaded on error.
         */
        reloadExtraLayer: debounce(function reloadExtraLayer() {
            const { map } = this;
            if (!map) {
                return;
            }

            const source = map.getSource(LAYER);
            if (!source) {
                // The layer is not visible - skip reload.
                return;
            }

            source.load();
        }, 1000),

        /**
         * Toggle of visibility layers control panel.
         */
        toggleLayersVisibility() {
            this.layersVisibility = !this.layersVisibility;
        },

        resetLayer() {
            const { map } = this;
            if (!map) {
                return;
            }

            try {
                removeLayer(map);
            } catch (e) {
                // Catch the situation when we are trying to manipulate
                // the map when style is not loaded. Simply skip the
                // further manipulation. The initial layer will be
                // loaded as soon as the style is loaded.
                return;
            }

            this.$nextTick(() => {
                addLayer(map, [this.tiles]);
            });
        },

        updateStartDate(date) {
            this.startDate = date;
            this.$emit('trackDateRangeChanged', {
                startDate: date,
                endDate: this.endDate,
            });
        },
        updateEndDate(date) {
            this.endDate = date;
            this.$emit('trackDateRangeChanged', {
                startDate: this.startDate,
                endDate: date,
            });
        },

        toggleDrawingMode() {
            if (this.isDrawingModeActive) {
                this.$emit('endDrawingMode');
            } else {
                this.$emit('startDrawingMode');
            }
        },
    },

    watch: {
        tiles() {
            this.resetLayer();
        },

        layer(newValue) {
            this.$emit('layerChanged', newValue);
            if (newValue !== LAYERS.TRACKS) {
                this.$emit('endDrawingMode');
            }
        },

        map(newMap) {
            if (!newMap) {
                return;
            }

            // Reload panorama layer when map sends out an error.
            newMap.on('error', (error) => {
                const { tile } = error;

                if (tile && tile.state === 'errored' && this.isPanoramaLayerVisible) {
                    this.reloadExtraLayer();
                }
            });

            newMap.once('styledata', () => {
                this.resetLayer();
            });
        },

        /**
         * Updates parent TTMap component so that the pointer cursor is
         * displayed correctly.
         */
        isExtraLayerVisible(visible) {
            this.$emit(visible ? 'shownExtraLayer' : 'hiddenExtraLayer');
        },
    },
    components: {
        DatePicker,
    },
};
</script>
